import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import SubtreeRetainer from 'flarum/common/utils/SubtreeRetainer';
import classList from 'flarum/common/utils/classList';
import Dropdown from 'flarum/common/components/Dropdown';
import Link from 'flarum/common/components/Link';
import highlight from 'flarum/common/helpers/highlight';
import slidable from 'flarum/forum/utils/slidable';
import Icon from 'flarum/common/components/Icon';
import abbreviateNumber from 'flarum/common/utils/abbreviateNumber';
import PollControls from '../../utils/PollControls';
import ItemList from 'flarum/common/utils/ItemList';
import listItems from 'flarum/common/helpers/listItems';
import PollViewPage from '../PollViewPage';
import PollDraftBadges from './PollDraftBadges';
/**
 * The `PollListItem` component shows a single poll in the
 * poll list.
 */
export default class PollListItem extends Component {
  constructor() {
    super(...arguments);
    /**
     * Ensures that the poll will not be redrawn
     * unless new data comes in.
     */
    _defineProperty(this, "subtree", void 0);
    _defineProperty(this, "poll", void 0);
    _defineProperty(this, "highlightRegExp", void 0);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.poll = this.attrs.poll;
    this.subtree = new SubtreeRetainer(() => this.poll.freshness, () => {
      const time = app.session.user && app.session.user.markedAllAsReadAt();
      return time && time.getTime();
    }, () => this.active());
  }
  elementAttrs() {
    return {
      className: classList('PollListItem', {
        active: this.active(),
        'PollListItem--hidden': this.poll.isHidden(),
        Slidable: 'ontouchstart' in window
      })
    };
  }
  view() {
    const controls = PollControls.controls(this.poll, this).toArray();
    const attrs = this.elementAttrs();
    return m("div", attrs, this.controlsView(controls), this.contentView(), this.slidableUnderneathView());
  }
  controlsView(controls) {
    return !!controls.length && m(Dropdown, {
      icon: "fas fa-ellipsis-v",
      className: "PollListItem-controls",
      menuClassName: "Dropdown-menu--right",
      buttonClassName: "Button Button--icon Button--flat",
      accessibleToggleLabel: app.translator.trans('fof-polls.forum.poll_controls.toggle_dropdown_accessible_label')
    }, controls);
  }
  slidableUnderneathView() {
    const isUnread = this.poll.isUnread();
    return m("span", {
      className: classList('Slidable-underneath Slidable-underneath--left Slidable-underneath--elastic', {
        disabled: !isUnread
      }),
      onclick: this.markAsRead.bind(this)
    }, m(Icon, {
      name: "fas fa-check"
    }));
  }
  contentView() {
    // const isUnread = this.poll.isUnread();
    // const isRead = this.poll.isRead();

    return (
      //   <div className={classList('PollListItem-content', 'Slidable-content', { unread: isUnread, read: isRead })}>
      m("div", {
        className: classList('PollListItem-content')
      }, this.mainView())
    );
  }
  mainView() {
    return m(Link, {
      href: app.route('fof.polls.view', {
        id: this.poll.id()
      }),
      className: "PollListItem-main"
    }, m("h2", {
      className: "PollListItem-title"
    }, highlight(this.pollQuestion(this.poll), this.highlightRegExp), this.draftBadges()), this.poll.subtitle() && m("p", {
      className: "PollListItem-subtitle helpText"
    }, this.pollSubtitle(this.poll)), m("ul", {
      className: "PollListItem-info"
    }, listItems(this.infoItems().toArray())));
  }
  draftBadges() {
    return m(PollDraftBadges, {
      poll: this.poll
    });
  }

  /**
   * Allow extensions to revise the question string.
   */
  pollQuestion(poll) {
    return poll.question();
  }

  /**
   * Allow extensions to revise the subtitle string.
   */
  pollSubtitle(poll) {
    return poll.subtitle();
  }
  oncreate(vnode) {
    super.oncreate(vnode);

    // If we're on a touch device, set up the discussion row to be slidable.
    // This allows the user to drag the row to either side of the screen to
    // reveal controls.
    if ('ontouchstart' in window) {
      const slidableInstance = slidable(this.element);
      this.$('.PollListItem-controls').on('hidden.bs.dropdown', () => slidableInstance.reset());
    }
  }
  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    return this.subtree.needsRebuild();
  }

  /**
   * Determine whether or not the discussion is currently being viewed.
   */
  active() {
    return app.current.matches(PollViewPage, {
      poll: this.poll
    });
  }

  /**
   * Mark the poll as read.
   */
  markAsRead() {
    if (this.poll.isUnread()) {
      this.poll.save({
        lastVotedNumber: this.poll.voteCount()
      });
      m.redraw();
    }
  }
  infoItems() {
    const items = new ItemList();
    const active = !this.poll.hasEnded();
    const activeView = this.poll.endDate() ? [m(Icon, {
      name: "fas fa-clock"
    }), ' ', active ? app.translator.trans('fof-polls.forum.days_remaining', {
      time: dayjs(this.poll.endDate()).fromNow()
    }) : app.translator.trans('fof-polls.forum.poll_ended')] : [m(Icon, {
      name: "fas fa-infinity"
    }), ' ', app.translator.trans('fof-polls.forum.poll_never_ends')];
    items.add('active', m("span", {
      className: classList('PollListItem-endStatus', {
        active
      })
    }, activeView));
    const voteCount = this.poll.voteCount();
    if (voteCount !== undefined) {
      items.add('voteCount', m("span", null, m(Icon, {
        name: "fas fa-poll fa-fw"
      }), [' ', app.translator.trans('fof-polls.forum.polls_count', {
        count: abbreviateNumber(voteCount)
      })]), 70);
    }
    return items;
  }
}
flarum.reg.add('fof-polls', 'forum/components/Poll/PollListItem', PollListItem);